import FW from './fw';
import Logger from './logger';
import ErrorCodes from './error-codes';
import LocalStorage from './local-storage';
import RmpConnection from '../../assets/rmp-connection';


export default class Utils {

  #rmp;
  #initialAutoplayRequest = false;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  get initialAutoplayRequest() {
    return this.#initialAutoplayRequest;
  }

  set initialAutoplayRequest(value) {
    this.#initialAutoplayRequest = value;
  }

  static initWindowRmpGlobals() {
    if (typeof window.rmpGlobals !== 'object') {
      window.rmpGlobals = {};
      window.rmpGlobals.rmpInstances = [];
      window.rmpGlobals.rmpStorage = null;
      window.rmpGlobals.fullscreenProxyEnabled = false;
      window.rmpGlobals.googleCastInitialized = false;
      window.rmpGlobals.forceRawConsoleLogs = false;
      window.rmpGlobals.rmpConnection = new RmpConnection();
      window.rmpGlobals.TCFv2 = {
        gdprApplies: '0',
        tcString: ''
      };
      window.rmpGlobals.USPData = '';
    }
  }

  static polyfillFullscreenEvents() {
    if (!window.rmpGlobals.fullscreenProxyEnabled) {
      window.rmpGlobals.fullscreenProxyEnabled = true;
      document.addEventListener('webkitfullscreenchange', FW.proxyFullscreenEvents);
      document.addEventListener('mozfullscreenchange', FW.proxyFullscreenEvents);
      document.addEventListener('MSFullscreenChange', FW.proxyFullscreenEvents);
    }
  }

  #filterFpsDrm() {
    // licenseResponseType
    const validResponseType = ['blob', 'json', 'text', 'arraybuffer'];
    let licenseResponseType = 'text';
    if (typeof this.#rmp.fpsDrm.licenseResponseType === 'string' &&
      validResponseType.indexOf(this.#rmp.fpsDrm.licenseResponseType) > -1) {
      licenseResponseType = this.#rmp.fpsDrm.licenseResponseType;
    }
    this.#rmp.fpsDrm.licenseResponseType = licenseResponseType;

    // licenseRequestHeaders
    if (!Array.isArray(this.#rmp.fpsDrm.licenseRequestHeaders)) {
      this.#rmp.fpsDrm.licenseRequestHeaders = [];
    }

    // certificateHeaders 
    if (!Array.isArray(this.#rmp.fpsDrm.certificateRequestHeaders)) {
      this.#rmp.fpsDrm.certificateRequestHeaders = [
        {
          name: 'Pragma',
          value: 'Cache-Control: no-cache'
        }, {
          name: 'Cache-Control',
          value: 'max-age=0'
        }
      ];
    }

    // extractContentId
    if (typeof this.#rmp.fpsDrm.extractContentId !== 'function') {
      this.#rmp.fpsDrm.extractContentId = FW.defaultExtractContentId;
    }

    // licenseRequestMessage 
    if (typeof this.#rmp.fpsDrm.licenseRequestMessage !== 'function') {
      this.#rmp.fpsDrm.licenseRequestMessage = FW.defaultLicenseRequestMessage;
    }

    // licenseRequestLoaded
    if (typeof this.#rmp.fpsDrm.licenseRequestLoaded !== 'function') {
      this.#rmp.fpsDrm.licenseRequestLoaded = FW.defaultLicenseRequestLoaded;
    }
  }

  detectSrc() {
    if (this.#rmp.audioOnly || this.#rmp.audioOnlyUseVideoLayout || this.#rmp.relatedAudioOnly || this.#rmp.playlistAudioOnly) {
      if (this.#rmp.src.m4a) {
        this.#rmp.hasM4a = true;
      }
      if (this.#rmp.src.mp3) {
        this.#rmp.hasMp3 = true;
      }
      if (this.#rmp.src.ogg) {
        this.#rmp.hasOgg = true;
      }
    } else {
      if (this.#rmp.src.mp4 && this.#rmp.src.mp4[0]) {
        this.#rmp.hasMp4 = true;
      }
      if (this.#rmp.src.webm && this.#rmp.src.webm[0]) {
        this.#rmp.hasWebM = true;
      }
    }
    if (this.#rmp.src.hls) {
      this.#rmp.hasHls = true;
    }
    if (this.#rmp.src.dash) {
      this.#rmp.hasDash = true;
    }
    // FPS
    if (this.#rmp.src.fps && this.#rmp.fpsDrm.certificatePath && this.#rmp.fpsDrm.processSpcPath) {
      this.#filterFpsDrm();
      this.#rmp.hasFps = true;
      this.#rmp.airplay = false;
    }
  }

  fireAutoplayFailure() {
    this.#rmp.viewableAutoplay = false;
    this.#rmp.autoplay = false;
    this.#rmp.autoplayResultKnown = true;
    this.#rmp.rmpUtils.warning(1005);
    this.#rmp.dispatch('autoplayfailure');

  }

  autoplayFailureRestorePlayUI() {
    this.#initialAutoplayRequest = false;
    if (this.#rmp.ads) {
      this.#rmp.rmpPlaybackManagement.unwireAdAutoplayEvents();
    }
    this.#rmp.rmpUIManagement.restorePlayUI();
    this.fireAutoplayFailure();
  }

  fireAutoplaySuccess() {
    this.#initialAutoplayRequest = false;
    if (this.#rmp.ads) {
      this.#rmp.rmpPlaybackManagement.unwireAdAutoplayEvents();
    }
    if (LocalStorage.available(this.#rmp.allowLocalStorage) && this.#rmp.autoplayCount !== Infinity) {
      try {
        if (localStorage.getItem('rmpAutoplayCount') !== null) {
          let rmpAutoplayCount = JSON.parse(localStorage.getItem('rmpAutoplayCount'));
          let storedValue = 0;
          if (rmpAutoplayCount === null || typeof rmpAutoplayCount.value !== 'number') {
            storedValue = this.#rmp.autoplayCount - 1;
          } else {
            storedValue = rmpAutoplayCount.value - 1;
          }
          if (storedValue < 0) {
            storedValue = 0;
          }
          localStorage.setItem('rmpAutoplayCount', JSON.stringify({ value: storedValue }));
        }
      } catch (warning) {
        console.warn(warning);
      }
    }
    this.#rmp.autoplayResultKnown = true;
    this.#rmp.dispatch('autoplaysuccess');
  }

  playPromise() {
    let initialAutoplayRequest = false;
    if (this.#rmp.autoplay && this.#initialAutoplayRequest) {
      initialAutoplayRequest = true;
      this.#initialAutoplayRequest = false;
    }
    const videoPlayPromise = this.#rmp.dom.video.play();
    // on Chrome 50+ play() returns a promise
    // https://developers.google.com/web/updates/2016/03/play-returns-promise
    // but not all browsers support this - so we just catch the potential Chrome error that 
    // may result if pause() is called in between - pause should overwrite play 
    // and in this case causes a promise rejection
    if (videoPlayPromise !== undefined) {
      videoPlayPromise.then(() => {
        Logger.print('info', 'videoPlayPromise on content has resolved');
        if (initialAutoplayRequest) {
          this.fireAutoplaySuccess();
        }
        if (this.#rmp.googleCastInit) {
          this.#rmp.googleCastInit = false;
          this.#rmp.pause();
        }
        this.#rmp.resolvePromise('play');
      }).catch(warning => {
        if (initialAutoplayRequest) {
          this.autoplayFailureRestorePlayUI();
        } else {
          this.#rmp.rmpUIManagement.restorePlayUI();
        }
        if (this.#rmp.googleCastInit) {
          this.#rmp.googleCastInit = false;
          this.#rmp.pause();
        }
        this.#rmp.rmpUtils.warning(1011, warning);
        this.#rmp.rejectPromise('play', this.#rmp.warningData);
      });
    } else {
      // we validate success when no promise play support
      if (initialAutoplayRequest) {
        Logger.print('info', 'play promise is not supported - using fallback');
        this.fireAutoplaySuccess();
        if (this.#rmp.googleCastInit) {
          this.#rmp.googleCastInit = false;
          this.#rmp.pause();
        }
      }
      this.#rmp.resolvePromise('play');
    }
  }

  fireLevelSwitchEvents() {
    this.#rmp.dispatch('qualitychanging');
    this.#rmp.dispatch('qualitychanged');
  }

  error(code, event, logOnly) {
    this.#rmp.innerErrorData = {
      code,
      fatal: true,
      message: '',
      event: null
    };
    if (ErrorCodes.hrErrorCode[code]) {
      this.#rmp.innerErrorData.message = ErrorCodes.hrErrorCode[code];
    }
    console.error(
      `Radiant Media Player fatal error - error.code: ${this.#rmp.innerErrorData.code} - error.message: ${this.#rmp.innerErrorData.message} - at ${FW.getNow() - this.#rmp.refTime} ms`
    );
    if (event) {
      console.error(event);
      this.#rmp.innerErrorData.event = event;
    }
    // in some instances (preload, error on playlist/related)
    // we may have a fatal error before ready event
    if (!this.#rmp.playerReady) {
      this.#rmp.googleCast = false;
      this.#rmp.rmpReadyManagement.minimalFire();
      this.#rmp.rejectPromise('init', this.#rmp.errorData);
    }
    if (logOnly) {
      this.#rmp.dispatch('error');
      return;
    }
    const errorNode = this.#rmp.dom.container.querySelector('.rmp-error');
    if (errorNode) {
      FW.removeElement(errorNode);
    }
    const newErrorNode = FW.createElement('div', 'rmp-error');
    if (!this.#rmp.dom.content) {
      this.#rmp.dom.content = FW.createElement('div', 'rmp-content');
      FW.insertBefore(this.#rmp.dom.container, this.#rmp.dom.content, this.#rmp.dom.container.firstChild);
    }
    newErrorNode.appendChild(FW.createElement('div', 'rmp-error-overlay'));
    const errorLabel = this.#rmp.labels.error;
    const terminal = FW.createElement('div', 'rmp-error-terminal');
    let terminalSting = `<div class="rmp-error-text-wrapper"><div class="rmp-error-text-header">`;
    if (this.#rmp.errorOnlyShowCustomText) {
      terminalSting += `${errorLabel.customErrorMessage}`;
    } else {
      terminalSting += `${errorLabel.error} `;
    }
    if (!this.#rmp.errorOnlyShowCustomText) {
      terminalSting += `<span class="rmp-error-code">${this.#rmp.innerErrorData.code}</span>`;
    }
    terminalSting += `</div>`;
    if (!this.#rmp.errorOnlyShowCustomText) {
      if (this.#rmp.innerErrorData.code === 107) {
        terminalSting += `<p class="rmp-error-output">${errorLabel.noSupportMessage}</p>`;
        if (errorLabel.noSupportInstallChrome !== '') {
          terminalSting += `<p class="rmp-error-output">${errorLabel.noSupportInstallChrome}</p>`;
        }
      } else if (errorLabel.customErrorMessage !== '') {
        terminalSting += `<p class="rmp-error-output">${errorLabel.customErrorMessage}</p>`;
      }
      terminalSting += `<p class="rmp-error-output">${this.#rmp.innerErrorData.message}</p>`;
    }
    terminalSting += `</div>`;
    terminal.innerHTML = terminalSting;
    newErrorNode.appendChild(terminal);
    this.#rmp.dom.content.appendChild(newErrorNode);
    // for no support this might be needed 
    if (!this.#rmp.dom.container.contains(this.#rmp.dom.content)) {
      this.#rmp.dom.container.appendChild(this.#rmp.dom.content);
    }
    let leaveModule = false;
    if ((this.#rmp.readingMp4 && this.#rmp.src.mp4.length > 0) || this.#rmp.hasPlaylist || this.#rmp.hasRelated) {
      leaveModule = true;
    }
    if (this.#rmp.changingSrc) {
      this.#rmp.changingSrc = false;
    }
    this.#rmp.rmpModuleManagement.closeAll();
    if (!leaveModule) {
      if (this.#rmp.readingPDorNative && this.#rmp.dom.video) {
        this.#rmp.dom.video.removeEventListener('error', this.#rmp.generateErrorFn, true);
      }
    }
    this.#rmp.dom.container.classList.add('rmp-error-ui');
    this.#rmp.dom.container.classList.remove('rmp-wait-for-duration');
    if (this.#rmp.rmpHlsJS) {
      this.#rmp.rmpHlsJS.destroy();
      this.#rmp.dispatch('error');
    } else if (this.#rmp.readingShaka) {
      if (this.#rmp.shakaPlayer && this.#rmp.rmpShakaPlayer) {
        this.#rmp.rmpShakaPlayer.destroy();
        let runCallback = true;
        this.#rmp.shakaPlayer.destroy().then(() => {
          if (runCallback) {
            runCallback = false;
            this.#rmp.dispatch('error');
          }
        });
        window.setTimeout(() => {
          if (runCallback) {
            runCallback = false;
            this.#rmp.dispatch('error');
          }
        }, 1000);
      } else {
        this.#rmp.dispatch('error');
      }
    } else {
      FW.clearNativeHtml5Buffer(this.#rmp.dom.video);
      this.#rmp.dispatch('error');
    }
    Logger.print('error', this.#rmp.innerErrorData.message, this.#rmp.innerErrorData);
  }

  warning(code, event) {
    this.#rmp.innerWarningData = {
      code,
      message: '',
      event: null,
      fatal: false
    };
    if (ErrorCodes.hrWarningCode[code]) {
      this.#rmp.innerWarningData.message = ErrorCodes.hrWarningCode[code];
    }
    if (event) {
      console.warn(event);
      this.#rmp.innerWarningData.event = event;
    }
    Logger.print('warning', this.#rmp.innerWarningData.message, this.#rmp.innerWarningData);
    this.#rmp.dispatch('warning');
  }

  updateDurationText(duration, currentTime) {
    let finalDuration = duration;
    if (this.#rmp.displayRemainingTimeInPlaceOfDuration && currentTime > -1) {
      finalDuration = duration - currentTime;
    }
    FW.setText(this.#rmp.dom.durationText, FW.readableTime(finalDuration / 1000));
  }

  refreshHtml5VideoErrorManagement() {
    this.#rmp.dom.video.removeEventListener('error', this.#rmp.generateErrorFn, true);
    this.#rmp.dom.video.addEventListener('error', this.#rmp.generateErrorFn, true);
  }

  refreshHtml5VideoEndedManagement() {
    this.#rmp.dom.video.removeEventListener('ended', this.#rmp.endedFn);
    this.#rmp.dom.video.addEventListener('ended', this.#rmp.endedFn);
  }
}
